import { firebase_app } from '../../../../Config/Config';
import { getCanonicalEventTimestamp } from '../../../../utils/eventTruth';
import { getReportingProxyUrl } from '../../../../utils/reportingClient';
import { loadUsers } from '../../Users/userFirebase';
import { AGENT_COLORS, toLocalDateValue } from './trackingReplayUtils';

export const DEFAULT_TRACKING_API = 'https://maxpark-firebase-sync-worker.onrender.com';

export const TRACKING_STATES = [
  'active',
  'paused',
  'disabled',
  'permission_disabled',
  'offline',
  'no_data',
];

const cleanOrigin = value => String(value || '').trim().replace(/\/+$/, '');
const numberOrNull = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const metadataObject = value => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);
const valueOf = (row, camel, snake) => row?.[camel] ?? row?.[snake];
const cleanIdentity = value => String(value || '').trim();
const identityKey = value => cleanIdentity(value).toLowerCase();
const displayNameForProfile = profile => cleanIdentity(
  profile.displayName
  || profile.fullName
  || profile.full_name
  || profile.name
  || profile.agentName
  || profile.agent_name
  || [profile.firstname || profile.firstName, profile.lastname || profile.lastName]
    .filter(Boolean)
    .join(' ')
);
const profileIdentifiers = profile => [
  profile.uid,
  profile.employeeid,
  profile.employeeID,
  profile.employeeId,
  profile.id,
  profile.email,
  profile.useremail,
].map(identityKey).filter(Boolean);
const trackingIdentityAliases = ({ id = '', metadata = {}, source = {} }) => [
  id,
  metadata.agentEmail,
  metadata.agent_email,
  metadata.email,
  metadata.userEmail,
  metadata.employeeid,
  metadata.employeeID,
  metadata.employeeId,
  source.agentEmail,
  source.agent_email,
  source.email,
  source.employeeid,
  source.employeeID,
  source.employeeId,
].map(identityKey).filter(Boolean);
const safeDisplayName = ({ profile = {}, metadata = {}, source = {}, id = '' }) => {
  const candidates = [
    displayNameForProfile(profile),
    metadata.displayName,
    metadata.fullName,
    metadata.name,
    source.display_name,
  ].map(cleanIdentity).filter(Boolean);
  const hiddenIdentities = new Set([identityKey(id), ...profileIdentifiers(profile)]);
  return candidates.find(candidate => (
    !candidate.includes('@') && !hiddenIdentities.has(identityKey(candidate))
  )) || 'Agent MaxPark';
};

export const getTrackingApiOrigin = configuredOrigin => cleanOrigin(
  configuredOrigin
  || process.env.REACT_APP_TRACKING_API
  || process.env.REACT_APP_REPORTING_API
  || DEFAULT_TRACKING_API
);

export const minuteInToronto = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const hour = Number(parts.find(part => part.type === 'hour')?.value || 0);
  const minute = Number(parts.find(part => part.type === 'minute')?.value || 0);
  return (hour * 60) + minute;
};

const normalizePosition = (row = {}, index = 0) => {
  const occurredAt = valueOf(row, 'occurredAt', 'occurred_at') || '';
  return {
    id: `${valueOf(row, 'agentId', 'agent_id') || 'agent'}-${valueOf(row, 'installationId', 'installation_id') || 'device'}-${valueOf(row, 'sequenceNumber', 'sequence_number') ?? index}`,
    agentId: String(valueOf(row, 'agentId', 'agent_id') || ''),
    installationId: String(valueOf(row, 'installationId', 'installation_id') || ''),
    sequenceNumber: Number(valueOf(row, 'sequenceNumber', 'sequence_number') || 0),
    occurredAt,
    minute: minuteInToronto(occurredAt),
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
    accuracyMeters: numberOrNull(valueOf(row, 'accuracyMeters', 'accuracy_meters')),
    source: String(row.source || ''),
    metadata: metadataObject(row.metadata),
  };
};

const normalizeEvent = (row = {}, index = 0) => {
  const occurredAt = valueOf(row, 'occurredAt', 'occurred_at') || '';
  const type = String(valueOf(row, 'eventType', 'event_type') || '');
  const labels = {
    tracking_started: 'Suivi démarré',
    tracking_paused: 'Suivi en pause',
    tracking_resumed: 'Suivi repris',
    tracking_disabled: 'Suivi désactivé',
    permission_disabled: 'Permission désactivée',
    offline: 'Hors ligne',
    online: 'En ligne',
    battery_low: 'Batterie faible',
  };
  return {
    id: String(valueOf(row, 'eventId', 'event_id') || `event-${index}`),
    agentId: String(valueOf(row, 'agentId', 'agent_id') || ''),
    type,
    label: labels[type] || type,
    reason: String(row.reason || ''),
    occurredAt,
    minute: minuteInToronto(occurredAt),
    metadata: metadataObject(row.metadata),
  };
};

const normalizeTicketActivityEvent = (row = {}, index = 0) => {
  const sourceOccurredAt = valueOf(row, 'occurredAt', 'occurred_at') || '';
  const type = String(valueOf(row, 'eventType', 'event_type') || '');
  const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(sourceOccurredAt);
  const parsedInstant = hasExplicitTimeZone ? Date.parse(sourceOccurredAt) : Number.NaN;
  const canonicalTimestamp = Number.isNaN(parsedInstant)
    ? getCanonicalEventTimestamp({
      id: valueOf(row, 'eventId', 'event_id'),
      type,
      occurredAt: sourceOccurredAt,
    })
    : parsedInstant;
  const occurredAt = canonicalTimestamp
    ? new Date(canonicalTimestamp).toISOString()
    : sourceOccurredAt;

  return {
    id: String(valueOf(row, 'eventId', 'event_id') || `ticket-event-${index}`),
    agentId: String(valueOf(row, 'agentId', 'agent_id') || ''),
    agentName: String(valueOf(row, 'agentName', 'agent_name') || ''),
    ticketId: String(valueOf(row, 'ticketId', 'ticket_id') || ''),
    type,
    occurredAt,
    minute: minuteInToronto(occurredAt),
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
  };
};

export const deriveTrackingState = ({
  position,
  event,
  now = new Date(),
  enforceFreshness = true,
} = {}) => {
  if (!position) return 'no_data';
  const eventType = event?.type || '';
  const positionTime = new Date(valueOf(position, 'occurredAt', 'occurred_at')).getTime();
  const eventTime = new Date(valueOf(event, 'occurredAt', 'occurred_at')).getTime();
  const eventApplies = (
    !Number.isFinite(positionTime)
    || !Number.isFinite(eventTime)
    || eventTime >= positionTime
  );
  if (eventApplies) {
    if (eventType === 'permission_disabled') return 'permission_disabled';
    if (eventType === 'tracking_disabled') return 'disabled';
    if (eventType === 'tracking_paused') return 'paused';
    if (eventType === 'offline') return 'offline';
  }
  if (enforceFreshness && (
    !Number.isFinite(positionTime)
    || now.getTime() - positionTime > 45 * 60 * 1000
  )) {
    return 'offline';
  }
  return 'active';
};

const requestJson = async (fetchImpl, url, token) => {
  const response = await fetchImpl(url, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Le suivi en direct est indisponible (${response.status}).`);
  }
  return body;
};

const uniquePositions = positions => {
  const byId = new Map();
  positions.forEach(position => byId.set(position.id, position));
  return [...byId.values()].sort((left, right) => (
    new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime()
  ));
};

export const buildTrackingDataset = ({
  date,
  profiles: profileRows = [],
  agents: agentRows = [],
  positions: positionRows = [],
  livePositions: liveRows = [],
  events: eventRows = [],
  ticketActivityEvents: ticketActivityRows = [],
  now = new Date(),
}) => {
  const profiles = profileRows.filter(profile => profile && typeof profile === 'object');
  const profileByAlias = new Map();
  profiles.forEach(profile => {
    profileIdentifiers(profile).forEach(key => profileByAlias.set(key, profile));
  });
  const canonicalAgentId = (id, source = {}) => {
    const rawId = cleanIdentity(id);
    const profile = trackingIdentityAliases({
      id: rawId,
      metadata: metadataObject(source.metadata),
      source,
    })
      .map(alias => profileByAlias.get(alias))
      .find(Boolean);
    return cleanIdentity(profile?.uid || profile?.employeeid || profile?.employeeID || rawId);
  };
  const positions = uniquePositions(
    [...positionRows, ...liveRows]
      .map(normalizePosition)
      .map(position => ({ ...position, agentId: canonicalAgentId(position.agentId, position) }))
      .filter(position => (
        position.agentId
        && position.latitude !== null
        && position.longitude !== null
      ))
  );
  const events = eventRows
    .map(normalizeEvent)
    .map(event => ({ ...event, agentId: canonicalAgentId(event.agentId, event) }))
    .filter(event => event.agentId);
  const ticketActivityEvents = ticketActivityRows
    .map(normalizeTicketActivityEvent)
    .map(event => ({ ...event, agentId: canonicalAgentId(event.agentId, event) }))
    .filter(event => (
      (event.type === 'ticket_created' || event.type === 'ticket_printed')
      && event.agentId
      && event.latitude !== null
      && event.longitude !== null
    ));
  const positionsByAgent = new Map();
  positions.forEach(position => {
    const list = positionsByAgent.get(position.agentId) || [];
    list.push(position);
    positionsByAgent.set(position.agentId, list);
  });
  const eventsByAgent = new Map();
  events.forEach(event => {
    const list = eventsByAgent.get(event.agentId) || [];
    list.push(event);
    eventsByAgent.set(event.agentId, list);
  });

  const agentIds = new Set([
    ...profiles.map(profile => canonicalAgentId(
      profile.uid || profile.employeeid || profile.employeeID
    )),
    ...agentRows.map(row => canonicalAgentId(valueOf(row, 'agentId', 'agent_id'), row)),
    ...positions.map(position => position.agentId),
    ...events.map(event => event.agentId),
  ]);
  agentIds.delete('');

  const agents = [...agentIds].map((id, index) => {
    const source = agentRows.find(row => (
      canonicalAgentId(valueOf(row, 'agentId', 'agent_id'), row) === id
    )) || {};
    const agentPositions = positionsByAgent.get(id) || [];
    const agentEvents = (eventsByAgent.get(id) || []).sort((left, right) => (
      new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime()
    ));
    const latestPosition = agentPositions[agentPositions.length - 1] || null;
    const latestEvent = agentEvents[agentEvents.length - 1] || null;
    const metadata = {
      ...metadataObject(source.metadata),
      ...metadataObject(latestPosition?.metadata),
    };
    const profile = trackingIdentityAliases({ id, metadata, source })
      .map(alias => profileByAlias.get(alias))
      .find(Boolean) || {};
    return {
      id,
      name: safeDisplayName({ profile, metadata, source, id }),
      role: String(
        profile.usertype
        || profile.userType
        || profile.role
        || metadata.role
        || source.role
        || 'agent'
      ).toLowerCase().includes('admin') ? 'admin' : 'agent',
      status: deriveTrackingState({
        position: latestPosition,
        event: latestEvent,
        now,
        enforceFreshness: date === toLocalDateValue(now),
      }),
      color: AGENT_COLORS[index % AGENT_COLORS.length],
      zone: String(metadata.zone || 'non_assignee'),
      lastPositionAt: latestPosition?.occurredAt || '',
    };
  });

  return {
    synthetic: false,
    date,
    timezone: 'America/Toronto',
    agents,
    positions,
    events,
    ticketActivityEvents,
  };
};

export const loadTrackingReplayData = async (options = {}) => {
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!fetchImpl) throw new Error('Le navigateur ne permet pas de charger le suivi en direct.');
  const user = options.user || firebase_app.auth().currentUser;
  if (!user) throw new Error('Session administrateur Firebase requise.');
  const token = await user.getIdToken();
  const origin = getTrackingApiOrigin(options.trackingApi);
  const date = options.date || toLocalDateValue();
  const query = `date=${encodeURIComponent(date)}&limit=5000`;

  const userLoader = options.loadUsersImpl || loadUsers;
  const [status, agents, history, events, live, profilesResult, ticketActivity] = await Promise.all([
    requestJson(fetchImpl, `${origin}/v1/tracking/status`),
    requestJson(fetchImpl, `${origin}/v1/tracking/agents`, token),
    requestJson(fetchImpl, `${origin}/v1/tracking/history?${query}`, token),
    requestJson(fetchImpl, `${origin}/v1/tracking/events?${query}`, token),
    requestJson(fetchImpl, `${origin}/v1/tracking/live`, token),
    userLoader({ forceRefresh: Boolean(options.forceProfileRefresh) }),
    requestJson(
      fetchImpl,
      getReportingProxyUrl('agent-timesheets', new URLSearchParams({
        date,
        eventLimit: '5000',
      })),
      token
    ).catch(() => ({ events: [] })),
  ]);
  if (!status.realDataAccepted) {
    throw new Error('Le backend de suivi n’accepte pas encore les positions réelles.');
  }

  return buildTrackingDataset({
    date,
    profiles: profilesResult.users || [],
    agents: agents.agents,
    positions: history.positions,
    livePositions: live.positions,
    events: events.events,
    ticketActivityEvents: ticketActivity.events,
    now: options.now || new Date(),
  });
};
